import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Minus, Plus, Trash2, Loader2, CheckCircle2, XCircle, Baby, GraduationCap, Star, Crown, Gem, Award,
  Users, Phone, UserPlus, BarChart3, BriefcaseMedical, Network, Wrench, FileText, CalendarDays, Sparkles,
} from "lucide-react";

const THEMES = [
  { match: /child/i, icon: Baby, head: "bg-pink-100 text-pink-900", iconCls: "text-pink-600 fill-pink-200", col: "bg-pink-50/70" },
  { match: /unlimited|e-?learning/i, icon: GraduationCap, head: "bg-gradient-to-b from-sky-500 to-blue-600 text-white", iconCls: "text-white", col: "bg-sky-50/80" },
  { match: /silver/i, icon: Star, head: "bg-gradient-to-b from-slate-200 to-slate-300 text-slate-900", iconCls: "text-slate-500 fill-slate-400", col: "bg-slate-50" },
  { match: /gold/i, icon: Crown, head: "bg-gradient-to-b from-amber-200 to-amber-300 text-amber-950", iconCls: "text-amber-700 fill-amber-500", col: "bg-amber-50/70" },
  { match: /platinum/i, icon: Gem, head: "bg-[#0b2a6f] text-white", iconCls: "text-white", col: "bg-blue-50/70" },
];
const FALLBACK = { icon: Award, head: "bg-primary/10 text-primary", iconCls: "text-primary", col: "bg-muted/30" };
export const planTheme = (title) => THEMES.find((t) => t.match.test(title)) || FALLBACK;

const ROW_ICONS = [
  [/e-?learning/i, GraduationCap], [/live/i, Users], [/call/i, Phone], [/recruit/i, UserPlus],
  [/mock|consult/i, BarChart3], [/first aid/i, BriefcaseMedical], [/network/i, Network],
  [/tool/i, Wrench], [/policy|template|resource/i, FileText], [/contract|month|term/i, CalendarDays],
];
const rowIcon = (label) => (ROW_ICONS.find(([re]) => re.test(label || "")) || [null, Sparkles])[1];
const isContract = (label) => /contract/i.test(label || "");
const shortTitle = (t) => t.replace(/ for Early Years Settings?/i, "");

const Cell = ({ v, contract }) => {
  const t = (v || "").trim();
  if (!t || t === "—" || t === "-") return <Minus className="h-4 w-4 text-slate-400 mx-auto" />;
  if (/^(x|✗|✕|no|none|not included)$/i.test(t)) return <XCircle className="h-6 w-6 mx-auto text-white fill-red-500" />;
  const Icon = contract ? CalendarDays : CheckCircle2;
  return (
    <span className="flex items-center gap-2.5 text-left text-slate-700">
      <Icon className={`h-6 w-6 shrink-0 ${contract ? "text-[#0b2a6f]" : "text-white fill-emerald-600"}`} />{t}
    </span>
  );
};

const PlanHead = ({ p }) => {
  const th = planTheme(p.title);
  return (
    <th className={`p-3 rounded-t-xl align-middle ${th.head}`} data-testid={`compare-plan-${p.id}`}>
      <div className="flex items-center gap-2.5 text-left">
        <th.icon className={`h-8 w-8 shrink-0 ${th.iconCls}`} />
        <div>
          <p className="font-heading font-bold leading-tight">{shortTitle(p.title)}</p>
          {p.price != null && <p className="text-xs font-medium opacity-80 mt-0.5">£{p.price}/mo ex VAT</p>}
        </div>
      </div>
    </th>
  );
};

const FeatureCell = ({ label, children }) => {
  const Icon = rowIcon(label);
  return (
    <td className="p-2 sticky left-0 z-10 bg-white">
      <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-2 sm:px-3 py-2.5 min-w-[7rem] sm:min-w-[13rem]">
        <span className="hidden sm:flex h-9 w-9 shrink-0 rounded-full bg-blue-100/70 items-center justify-center"><Icon className="h-4 w-4 text-[#0b2a6f]" /></span>
        {children || <span className="font-medium text-slate-800">{label}</span>}
      </div>
    </td>
  );
};

const CompareTable = ({ data, renderCell, renderLabel, extraHead, extraCell, footer }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[900px] text-sm border-separate border-spacing-x-1.5 border-spacing-y-1" data-testid="compare-table">
      <thead>
        <tr>
          <th className="p-3 rounded-t-xl bg-[#0b2a6f] text-white text-left font-heading text-base sticky left-0 z-20 w-32 sm:w-56">Feature</th>
          {data.plans.map((p) => <PlanHead key={p.id} p={p} />)}
          {extraHead}
        </tr>
      </thead>
      <tbody>
        {data.rows.map((r, i) => (
          <tr key={i}>
            <FeatureCell label={r.label}>{renderLabel?.(r, i)}</FeatureCell>
            {data.plans.map((p) => (
              <td key={p.id} className={`p-3 rounded-md ${planTheme(p.title).col}`}>{renderCell(r, i, p)}</td>
            ))}
            {extraCell?.(r, i)}
          </tr>
        ))}
        {footer}
      </tbody>
    </table>
  </div>
);

export const CompareMembershipsDialog = ({ onClose, onChoose }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/shop/membership-compare").then(({ data: d }) => setData(d)).catch(() => setData({ plans: [], rows: [] })); }, []);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-auto" data-testid="compare-memberships-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#0b2a6f]">Compare memberships</DialogTitle></DialogHeader>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
          <CompareTable
            data={data}
            renderCell={(r, i, p) => <Cell v={r.values[p.id]} contract={isContract(r.label)} />}
            footer={(
              <tr>
                <td className="sticky left-0 bg-white" />
                {data.plans.map((p) => (
                  <td key={p.id} className="p-3 text-center">
                    <Button size="sm" className="rounded-full px-6" onClick={() => onChoose(p.id)} data-testid={`compare-subscribe-${p.id}`}>Subscribe</Button>
                  </td>
                ))}
              </tr>
            )}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const CompareEditorDialog = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/shop/membership-compare").then(({ data: d }) => setData(d)); }, []);
  const setRow = (i, row) => setData({ ...data, rows: data.rows.map((r, j) => (j === i ? row : r)) });
  const save = async () => {
    setSaving(true);
    try { await api.put("/membership-compare", { rows: data.rows }); toast.success("Comparison table saved"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-auto" data-testid="compare-editor-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-[#0b2a6f]">Edit membership comparison</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Prices and plans come from your published monthly memberships. Leave a cell blank or type "—" to show a dash, or type "x" to show a red cross.</p>
        {!data ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /> : (
          <>
            <CompareTable
              data={data}
              renderLabel={(r, i) => <Input value={r.label} onChange={(e) => setRow(i, { ...r, label: e.target.value })} className="bg-white" data-testid={`compare-row-${i}-label`} />}
              renderCell={(r, i, p) => <Input value={r.values[p.id] || ""} onChange={(e) => setRow(i, { ...r, values: { ...r.values, [p.id]: e.target.value } })} className="bg-white/80" data-testid={`compare-row-${i}-${p.id}`} />}
              extraHead={<th className="w-10" />}
              extraCell={(r, i) => <td><Button variant="ghost" size="sm" onClick={() => setData({ ...data, rows: data.rows.filter((_, j) => j !== i) })} data-testid={`compare-row-${i}-delete`}><Trash2 className="h-4 w-4" /></Button></td>}
            />
            <Button variant="outline" size="sm" className="mt-2 self-start" onClick={() => setData({ ...data, rows: [...data.rows, { label: "", values: {} }] })} data-testid="compare-add-row"><Plus className="h-4 w-4 mr-1" />Add row</Button>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !data} className="bg-[#0b2a6f] hover:bg-[#0b2a6f]/90" data-testid="compare-save-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
